import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  Boxes,
  CheckCircle2,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UsersRound,
  X,
} from 'lucide-react'
import {Modal} from 'react-bootstrap'
import Select from 'react-select'

import * as API from '../../../api'
import {useAccessMatrixFilters} from '../../../components/AccessMatrixFilters'
import UserBulkPermissionsModal from '../UserBulkPermissionsModal'
import {ConfirmModal} from './ConfirmModal'

function initials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
}

function normalizeFeatureData(data: any) {
  const users = data?.users || []
  const features = data?.features || []
  const userIds = new Set(users.map((user: any) => user.id_user))
  const featureIds = new Set(features.map((feature: any) => feature.id_feature))
  const uniquePermissions = new Map<string, any>()

  ;(data?.permissions || []).forEach((permission: any) => {
    if (!userIds.has(permission.id_user) || !featureIds.has(permission.id_feature)) return
    uniquePermissions.set(`${permission.id_user}-${permission.id_feature}`, permission)
  })

  return {...data, users, features, permissions: Array.from(uniquePermissions.values())}
}

export const FeatureMatrix = () => {
  const {
    selectedEntities: selectedUsers,
    setSelectedEntities: setSelectedUsers,
    featureQuery,
    setFeatureQuery,
    accessFilter,
    setAccessFilter,
  } = useAccessMatrixFilters()
  const [menu, setMenu] = useState<any>()
  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [savingCells, setSavingCells] = useState<Set<string>>(new Set())
  const [bulkSaving, setBulkSaving] = useState(false)
  const [selectedMatrixUser, setSelectedMatrixUser] = useState<any>(null)
  const [showUserFeaturesModal, setShowUserFeaturesModal] = useState(false)
  const [userBulkSaving, setUserBulkSaving] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [confirmModal, setConfirmModal] = useState<any>({
    isOpen: false,
    feature: null,
    action: 'activate' as 'activate' | 'deactivate',
    count: 0,
  })

  const getFeatures = async (showLoading = true) => {
    if (showLoading) setLoading(true)
    setLoadError(false)

    try {
      const result = await API.getUserFeatures()
      const normalizedData = normalizeFeatureData(result.data)
      setMenu(normalizedData)
      if (result.success) {
        setUsers(
          normalizedData.users.map((item: any) => ({
            value: item.id_user,
            label: item.name,
            ...item,
          }))
        )
      }
    } catch {
      setLoadError(true)
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  useEffect(() => {
    getFeatures()
  }, [])

  const filteredFeatures = useMemo(() => {
    const normalizedQuery = featureQuery.trim().toLocaleLowerCase('pt-BR')
    if (!normalizedQuery) return menu?.features || []
    return (menu?.features || []).filter((feature: any) =>
      `${feature.title || ''} ${feature.name || ''}`
        .toLocaleLowerCase('pt-BR')
        .includes(normalizedQuery)
    )
  }, [featureQuery, menu?.features])

  const hasPermission = useCallback(
    (userId: number, featureId: number) => {
      return Boolean(
        menu?.permissions?.some(
          (permission: any) => permission.id_feature === featureId && permission.id_user === userId
        )
      )
    },
    [menu?.permissions]
  )

  const displayedUsers = useMemo(() => {
    const selectedIds = new Set(selectedUsers.map((user) => `${user.value}`))
    const baseUsers = (menu?.users || []).filter(
      (user: any) => !selectedIds.size || selectedIds.has(`${user.id_user}`)
    )
    if (accessFilter === 'all' || !filteredFeatures.length) return baseUsers

    return baseUsers.filter((user: any) => {
      const accessCount = filteredFeatures.filter((feature: any) =>
        hasPermission(user.id_user, feature.id_feature)
      ).length
      if (accessFilter === 'full') return accessCount === filteredFeatures.length
      if (accessFilter === 'partial')
        return accessCount > 0 && accessCount < filteredFeatures.length
      return accessCount === 0
    })
  }, [accessFilter, filteredFeatures, hasPermission, menu?.users, selectedUsers])

  const permissionCount = menu?.permissions?.length || 0
  const possiblePermissions = (menu?.users?.length || 0) * (menu?.features?.length || 0)
  const coverage = possiblePermissions
    ? Math.round((permissionCount / possiblePermissions) * 100)
    : 0

  const handleFeatureClick = (feature: any) => {
    if (!displayedUsers.length) return
    const allChecked = displayedUsers.every((user: any) =>
      hasPermission(user.id_user, feature.id_feature)
    )

    setConfirmModal({
      isOpen: true,
      feature,
      action: allChecked ? 'deactivate' : 'activate',
      count: displayedUsers.length,
    })
  }

  const handleConfirmFeature = async () => {
    const {feature, action} = confirmModal
    if (!feature || !displayedUsers.length) return
    setBulkSaving(true)
    setFeedback('')

    try {
      const responses = await Promise.all(
        displayedUsers.map((user: any) =>
          action === 'activate'
            ? API.addFeatureUserPermission({id_feature: feature.id_feature, guid: user.guid})
            : API.removeFeatureUserPermission({id_feature: feature.id_feature, guid: user.guid})
        )
      )
      if (responses.some((response: any) => response?.success === false)) {
        throw new Error('Bulk feature update failed')
      }
      await getFeatures(false)
      setFeedback(
        `${feature.title} foi ${action === 'activate' ? 'liberada' : 'removida'} para ${
          displayedUsers.length
        } usuário(s).`
      )
    } catch {
      setFeedback('Não foi possível concluir a alteração em massa. Tente novamente.')
    } finally {
      setBulkSaving(false)
      setConfirmModal({isOpen: false, feature: null, action: 'activate', count: 0})
    }
  }

  const handlePermissionChange = async (feature: any, user: any, checked: boolean) => {
    const cellKey = `${feature.id_feature}-${user.guid}`
    const permission = {id_feature: feature.id_feature, id_user: user.id_user}
    const previousPermissions = menu.permissions || []
    setSavingCells((current) => new Set(current).add(cellKey))
    setFeedback('')
    setMenu((current: any) => ({
      ...current,
      permissions: checked
        ? [...current.permissions, permission]
        : current.permissions.filter(
            (item: any) =>
              !(item.id_feature === feature.id_feature && item.id_user === user.id_user)
          ),
    }))

    try {
      const response = checked
        ? await API.addFeatureUserPermission({id_feature: feature.id_feature, guid: user.guid})
        : await API.removeFeatureUserPermission({id_feature: feature.id_feature, guid: user.guid})
      if (response?.success === false) throw new Error('Feature update failed')
      await getFeatures(false)
    } catch {
      setMenu((current: any) => ({...current, permissions: previousPermissions}))
      await getFeatures(false)
      setFeedback('Não foi possível salvar uma das alterações. Tente novamente.')
    } finally {
      setSavingCells((current) => {
        const next = new Set(current)
        next.delete(cellKey)
        return next
      })
    }
  }

  const handleUserFeatureRequest = async (selectAll: boolean) => {
    if (!selectedMatrixUser) return
    const targets = (menu?.features || []).filter(
      (feature: any) => hasPermission(selectedMatrixUser.id_user, feature.id_feature) !== selectAll
    )
    setUserBulkSaving(true)
    setFeedback('')

    try {
      const responses = await Promise.all(
        targets.map((feature: any) =>
          selectAll
            ? API.addFeatureUserPermission({
                id_feature: feature.id_feature,
                guid: selectedMatrixUser.guid,
              })
            : API.removeFeatureUserPermission({
                id_feature: feature.id_feature,
                guid: selectedMatrixUser.guid,
              })
        )
      )
      if (responses.some((response: any) => response?.success === false)) {
        throw new Error('Bulk user feature update failed')
      }
      await getFeatures(false)
      setFeedback(
        `${
          selectAll
            ? 'Todas as funcionalidades foram concedidas'
            : 'Todas as funcionalidades foram removidas'
        } para ${selectedMatrixUser.name}.`
      )
      setShowUserFeaturesModal(false)
    } catch {
      await getFeatures(false)
      setFeedback('Não foi possível concluir a alteração para este usuário. Tente novamente.')
    } finally {
      setUserBulkSaving(false)
    }
  }

  const selectedMatrixUserActiveCount = selectedMatrixUser
    ? (menu?.features || []).filter((feature: any) =>
        hasPermission(selectedMatrixUser.id_user, feature.id_feature)
      ).length
    : 0

  const hasFilters = Boolean(selectedUsers.length || featureQuery || accessFilter !== 'all')

  return (
    <section className='ua-section'>
      <Modal
        id='modal_user_features'
        tabIndex={-1}
        dialogClassName='modal-dialog modal-dialog-centered mw-500px ua-admin-modal-dialog'
        show={showUserFeaturesModal}
        onHide={() => !userBulkSaving && setShowUserFeaturesModal(false)}
      >
        {selectedMatrixUser && (
          <UserBulkPermissionsModal
            user={selectedMatrixUser}
            scopeLabel='Funcionalidades'
            itemLabel='funcionalidades'
            totalCount={menu?.features?.length || 0}
            activeCount={selectedMatrixUserActiveCount}
            loading={userBulkSaving}
            onChange={handleUserFeatureRequest}
            onClose={() => setShowUserFeaturesModal(false)}
          />
        )}
      </Modal>

      <header className='ua-section__heading'>
        <div className='ua-section__heading-copy'>
          <span className='ua-section__heading-icon'>
            <Boxes size={20} />
          </span>
          <div>
            <h2>Funcionalidades</h2>
            <p>Libere recursos complementares por usuário, com edição individual ou em massa.</p>
          </div>
        </div>
        <span className='ua-badge ua-badge--success'>Alterações salvas automaticamente</span>
      </header>

      {loading ? (
        <div className='ua-card ua-loading'>
          <span className='ua-spinner' /> Carregando funcionalidades…
        </div>
      ) : loadError ? (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <Boxes size={22} />
            </span>
            <h3>Não foi possível carregar as funcionalidades</h3>
            <p>Tente novamente para acessar a matriz de recursos.</p>
            <button
              type='button'
              className='ua-secondary-button mt-4'
              onClick={() => getFeatures()}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      ) : menu?.features?.length ? (
        <>
          <div className='ua-stats'>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <UsersRound size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{menu.users?.length || 0}</strong>
                <small className='ua-stat__label'>Usuários na matriz</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <Boxes size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{menu.features.length}</strong>
                <small className='ua-stat__label'>Funcionalidades disponíveis</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <CheckCircle2 size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{permissionCount}</strong>
                <small className='ua-stat__label'>Acessos concedidos</small>
              </span>
            </div>
            <div className='ua-stat'>
              <span className='ua-stat__icon'>
                <ShieldCheck size={18} />
              </span>
              <span>
                <strong className='ua-stat__value'>{coverage}%</strong>
                <small className='ua-stat__label'>Cobertura de acesso</small>
              </span>
            </div>
          </div>

          <div className='ua-toolbar'>
            <div className='ua-field ua-field--wide'>
              <label htmlFor='feature-user-filter'>Usuários</label>
              <Select
                inputId='feature-user-filter'
                isMulti
                options={users}
                value={selectedUsers}
                closeMenuOnSelect={false}
                placeholder='Todos os usuários'
                noOptionsMessage={() => 'Nenhum usuário encontrado'}
                onChange={(selected: any) => setSelectedUsers(selected || [])}
                classNamePrefix='admin-select'
              />
            </div>
            <div className='ua-field'>
              <label htmlFor='feature-name-filter'>Funcionalidade</label>
              <div className='ua-search'>
                <Search size={15} />
                <input
                  id='feature-name-filter'
                  value={featureQuery}
                  placeholder='Buscar recurso'
                  onChange={(event) => setFeatureQuery(event.target.value)}
                />
                {featureQuery && (
                  <button
                    type='button'
                    className='ua-search__clear'
                    aria-label='Limpar busca de funcionalidade'
                    onClick={() => setFeatureQuery('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className='ua-field'>
              <label htmlFor='feature-access-filter'>Nível de acesso</label>
              <div className='ua-select'>
                <select
                  id='feature-access-filter'
                  value={accessFilter}
                  onChange={(event) => setAccessFilter(event.target.value)}
                >
                  <option value='all'>Todos</option>
                  <option value='full'>Acesso completo</option>
                  <option value='partial'>Acesso parcial</option>
                  <option value='none'>Sem acesso</option>
                </select>
              </div>
            </div>
            {hasFilters && (
              <button
                type='button'
                className='ua-secondary-button'
                onClick={() => {
                  setSelectedUsers([])
                  setFeatureQuery('')
                  setAccessFilter('all')
                }}
              >
                <X size={14} /> Limpar
              </button>
            )}
          </div>

          {feedback && (
            <div
              className={`ua-feedback ${
                feedback.startsWith('Não foi possível') ? 'ua-feedback--danger' : ''
              }`}
            >
              {feedback}
            </div>
          )}

          <div className='ua-card'>
            <div className='ua-card__header'>
              <div>
                <h3>Matriz de funcionalidades</h3>
                <p>
                  {displayedUsers.length} usuário(s) · {filteredFeatures.length} funcionalidade(s)
                </p>
              </div>
              <span className='ua-matrix-tip'>
                Clique em uma coluna ou usuário para aplicar em massa
              </span>
            </div>

            {displayedUsers.length && filteredFeatures.length ? (
              <div className='ua-table-wrap ua-matrix-wrap'>
                <table className='ua-table ua-matrix'>
                  <thead>
                    <tr>
                      <th className='ua-sticky-column'>Usuário</th>
                      {filteredFeatures.map((feature: any) => (
                        <th key={feature.id_feature} className='ua-matrix__permission'>
                          <button
                            type='button'
                            className='ua-matrix-header-button'
                            title={`Aplicar ${feature.title} aos usuários visíveis`}
                            onClick={() => handleFeatureClick(feature)}
                          >
                            <SlidersHorizontal size={13} />
                            <span>{feature.title}</span>
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayedUsers.map((user: any) => (
                      <tr key={user.guid}>
                        <td className='ua-sticky-column'>
                          <button
                            type='button'
                            className='ua-user-trigger'
                            title={`Gerenciar todas as funcionalidades de ${user.name}`}
                            onClick={() => {
                              setSelectedMatrixUser(user)
                              setShowUserFeaturesModal(true)
                            }}
                          >
                            <span className='ua-user-cell'>
                              <span className='ua-avatar'>
                                {user.src ? <img src={user.src} alt='' /> : initials(user.name)}
                              </span>
                              <span className='ua-user-cell__copy'>
                                <strong>{user.name}</strong>
                                <small>{user.email || 'Usuário da empresa'}</small>
                              </span>
                            </span>
                            <span className='ua-user-trigger__action'>
                              <SlidersHorizontal size={14} />
                            </span>
                          </button>
                        </td>
                        {filteredFeatures.map((feature: any) => {
                          const cellKey = `${feature.id_feature}-${user.guid}`
                          return (
                            <td key={cellKey} className='ua-matrix__cell'>
                              <label className='ua-switch'>
                                <input
                                  type='checkbox'
                                  checked={hasPermission(user.id_user, feature.id_feature)}
                                  disabled={savingCells.has(cellKey)}
                                  aria-label={`${feature.title} para ${user.name}`}
                                  onChange={(event) =>
                                    handlePermissionChange(feature, user, event.target.checked)
                                  }
                                />
                                <span />
                              </label>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='ua-empty ua-empty--inside'>
                <div>
                  <span className='ua-empty__icon'>
                    <Search size={22} />
                  </span>
                  <h3>Nenhum resultado nesta matriz</h3>
                  <p>Ajuste os filtros para visualizar outras combinações de acesso.</p>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className='ua-empty'>
          <div>
            <span className='ua-empty__icon'>
              <Boxes size={22} />
            </span>
            <h3>Nenhuma funcionalidade disponível</h3>
            <p>O plano atual da empresa não possui funcionalidades adicionais configuradas.</p>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({...confirmModal, isOpen: false})}
        onConfirm={handleConfirmFeature}
        title={confirmModal.feature?.title || ''}
        action={confirmModal.action}
        count={confirmModal.count}
        loading={bulkSaving}
      />
    </section>
  )
}
